import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getDirectory, type DirectoryEntry } from "../../api/directory";
import { useAppContext, useSessionSnapshot } from "../../app/permissions";
import { useServices } from "../../app/services";
import { hiddenOrdinals, NAME_HIDDEN, staffLabel, type NameKind } from "../../lib/names";

export interface Person { id: string; name: string; isTrainer: boolean; active: boolean }

/**
 * Every staff name on a screen comes from here (or from a `trainerName`/`ownerName`/`assigneeName` the server already
 * redacted). `GET /directory` needs no lever, and it names a person only for a role that holds the right name lever, so
 * a front desk without `staff.view` still reads trainer names with `staff.see_trainer_names`.
 */
export function useDirectory() {
  const { client } = useServices();
  const snap = useSessionSnapshot();
  const ctx = useAppContext();
  const enabled = snap.status === "scoped";
  const q = useQuery({ queryKey: ["directory", snap.businessId, snap.branchId], queryFn: () => getDirectory(client), enabled, retry: false, staleTime: 5 * 60_000 });
  const myId = ctx.me?.staff.id ?? null;
  const myName = ctx.me?.staff.name ?? null;

  return useMemo(() => {
    const entries: DirectoryEntry[] = q.data ?? [];
    const byId = new Map(entries.map((e) => [e.id, e]));
    const ordinals = hiddenOrdinals(entries, myId);
    /** Text for one person. `known` is a name the record itself carried (already redacted by the server). */
    const nameOf = (id: string | null | undefined, kind: NameKind = "staff", known?: string | null): string | null => {
      if (!id) return null;
      const e = byId.get(id);
      return staffLabel({ id, myId, myName, name: e?.name ?? known ?? null, kind, isTrainer: e?.isTrainer, ordinal: ordinals.get(id) ?? null });
    };
    /** People the role may name, active only: owner and assignee pickers. Empty when names are hidden. */
    const people: Person[] = entries.filter((e) => e.active && e.name).map((e) => ({ id: e.id, name: e.name as string, isTrainer: e.isTrainer, active: true }));
    /**
     * Active people for a trainer picker: a hidden name reads "Trainer 1", "Trainer 2", never an id. You are always
     * listed by name. A person whose name is hidden is offered only if they already are a trainer, so a picker is
     * never a column of identical, unrecognisable "Staff member" rows.
     */
    const trainerOptions = entries
      .filter((e) => e.active && (e.name || e.isTrainer || e.id === myId))
      .map((e) => ({ id: e.id, name: e.id === myId ? (myName ?? NAME_HIDDEN.you) : staffLabel({ id: e.id, name: e.name, kind: "trainer", isTrainer: e.isTrainer, ordinal: ordinals.get(e.id) ?? null }) }));
    return {
      entries,
      people,
      trainerOptions,
      nameOf,
      /** Is this person's name readable by you? (Yourself always is.) */
      isNamed: (id: string) => id === myId || !!byId.get(id)?.name,
      pending: enabled && q.isPending,
      error: q.error,
      refetch: q.refetch,
    };
  }, [q.data, q.isPending, q.error, q.refetch, enabled, myId, myName]);
}
